/**
 * Initial BEX LOOP Scene Library (spec sections 11 & 12).
 *
 * A scene is a *reusable visual asset definition*, not an episode.
 * Definitions exist before assets do:
 *
 *   assetType: 'procedural' -> rendered live by the Canvas scene engine
 *   assetType: 'video'      -> an imported/generated clip in the library
 *   assetType: 'none'       -> no asset yet; UI shows generation instructions
 *
 * `proceduralId` maps to a renderer in src/scenes/procedural/index.js.
 * Nothing here ships third-party stock footage - every visual is either
 * procedural (drawn by this app) or supplied by the user.
 */

/** @typedef {'procedural'|'video'|'none'} AssetType */

export const SCENE_SCHEMA_VERSION = 1;

export const SCENES = [
  {
    id: 'taxi-night',
    name: 'Taxi Night',
    description:
      'Interior of a moving taxi at night. Camera sits on the passenger side, city lights and headlights slide across the windows, occasional reflections on the glass. The driver is out of frame.',
    tags: ['taxi', 'night', 'urban', 'headphones', 'vehicle', 'city'],
    moods: ['DARK', 'URBAN', 'NIGHT', 'SERIOUS'],
    environments: ['vehicle', 'urban', 'night'],
    cameraStyle: 'dashboard',
    visualStyle: 'cinematic-realistic',
    duration: 10,
    loopable: true,
    loopQuality: 'unknown',
    assetType: 'procedural',
    proceduralId: 'taxi-interior',
    assetUrl: null,
    thumbnailUrl: null,
    promptIds: ['taxi-night-cinematic', 'taxi-night-rain', 'taxi-night-urban', 'taxi-night-tehran', 'taxi-night-minimal'],
    audioEffect: 'headphones',
    enabled: true,
    weight: 10,
    lastUsed: null,
    usageCount: 0,
    license: { kind: 'procedural', creator: 'BEX LOOP', attribution: 'none required' },
  },
  {
    id: 'metro',
    name: 'Metro',
    description:
      'Inside a metro carriage. Ceiling strip lights pass overhead, blurred platform lights move past the far windows, the carriage sways very slightly. No passengers in focus.',
    tags: ['metro', 'subway', 'train', 'transit', 'urban', 'headphones'],
    moods: ['URBAN', 'SERIOUS', 'TECH', 'DARK'],
    environments: ['vehicle', 'urban', 'underground'],
    cameraStyle: 'window-fixed',
    visualStyle: 'documentary',
    duration: 10,
    loopable: true,
    loopQuality: 'unknown',
    assetType: 'procedural',
    proceduralId: 'metro',
    assetUrl: null,
    thumbnailUrl: null,
    promptIds: ['metro-cinematic', 'metro-minimal'],
    audioEffect: 'headphones',
    enabled: true,
    weight: 8,
    lastUsed: null,
    usageCount: 0,
    license: { kind: 'procedural', creator: 'BEX LOOP', attribution: 'none required' },
  },
  {
    id: 'cafe-rain',
    name: 'Caf\u00e9 Rain',
    description:
      'Quiet caf\u00e9 table by a large window on a rainy afternoon. Rain streaks down the glass, warm interior light, distant out-of-focus street movement, a cup on the table, nobody in frame.',
    tags: ['cafe', 'rain', 'window', 'calm', 'indoor', 'coffee'],
    moods: ['CALM', 'NOSTALGIC', 'SERIOUS'],
    environments: ['indoor', 'urban'],
    cameraStyle: 'static',
    visualStyle: 'analog-film',
    duration: 12,
    loopable: true,
    loopQuality: 'unknown',
    assetType: 'none',
    proceduralId: null,
    assetUrl: null,
    thumbnailUrl: null,
    promptIds: ['cafe-rain-cinematic', 'cafe-rain-minimal'],
    audioEffect: 'room',
    enabled: true,
    weight: 7,
    lastUsed: null,
    usageCount: 0,
    license: { kind: 'unassigned', creator: null, attribution: null },
  },
  {
    id: 'car-pov',
    name: 'Car POV',
    description:
      'Point-of-view from the driver seat on an empty road at dusk. Dashes of road marking flow toward the camera, dashboard glow at the bottom of frame, steady horizon.',
    tags: ['car', 'road', 'pov', 'driving', 'night', 'motion'],
    moods: ['DARK', 'ENERGETIC', 'NIGHT'],
    environments: ['vehicle', 'road'],
    cameraStyle: 'dashboard',
    visualStyle: 'cinematic-realistic',
    duration: 10,
    loopable: true,
    loopQuality: 'unknown',
    assetType: 'none',
    proceduralId: null,
    assetUrl: null,
    thumbnailUrl: null,
    promptIds: ['car-pov-cinematic'],
    audioEffect: 'car_radio',
    enabled: true,
    weight: 8,
    lastUsed: null,
    usageCount: 0,
    license: { kind: 'unassigned', creator: null, attribution: null },
  },
  {
    id: 'gaming-room',
    name: 'Gaming Room',
    description:
      'Dark gaming room lit only by monitor glow. RGB spill on the desk and wall, a mechanical keyboard in soft focus, dust in the light beam, no people.',
    tags: ['gaming', 'room', 'rgb', 'desk', 'tech', 'night'],
    moods: ['TECH', 'FUTURISTIC', 'DARK', 'ENERGETIC'],
    environments: ['indoor', 'night'],
    cameraStyle: 'static',
    visualStyle: 'cinematic-realistic',
    duration: 10,
    loopable: true,
    loopQuality: 'unknown',
    assetType: 'none',
    proceduralId: null,
    assetUrl: null,
    thumbnailUrl: null,
    promptIds: ['gaming-room-cinematic'],
    audioEffect: 'room',
    enabled: true,
    weight: 7,
    lastUsed: null,
    usageCount: 0,
    license: { kind: 'unassigned', creator: null, attribution: null },
  },
  {
    id: 'pizza-kitchen',
    name: 'Pizza Kitchen',
    description:
      'Warm commercial kitchen at night. Oven glow, steam drifting upward, stainless steel reflections, hands-free - only food and equipment in frame.',
    tags: ['kitchen', 'food', 'warm', 'indoor', 'steam', 'work'],
    moods: ['NOSTALGIC', 'CALM', 'SERIOUS'],
    environments: ['indoor'],
    cameraStyle: 'handheld-subtle',
    visualStyle: 'documentary',
    duration: 12,
    loopable: true,
    loopQuality: 'unknown',
    assetType: 'none',
    proceduralId: null,
    assetUrl: null,
    thumbnailUrl: null,
    promptIds: ['pizza-kitchen-cinematic'],
    audioEffect: 'room',
    enabled: true,
    weight: 5,
    lastUsed: null,
    usageCount: 0,
    license: { kind: 'unassigned', creator: null, attribution: null },
  },
  {
    id: 'rainy-window',
    name: 'Rainy Window',
    description:
      'Close-up of rain running down a window at night. Out-of-focus city bokeh behind the glass, slow droplet trails, gentle refocus breathing.',
    tags: ['rain', 'window', 'night', 'calm', 'abstract', 'bokeh'],
    moods: ['CALM', 'NIGHT', 'DARK', 'NOSTALGIC'],
    environments: ['indoor', 'night'],
    cameraStyle: 'static',
    visualStyle: 'cinematic-realistic',
    duration: 10,
    loopable: true,
    loopQuality: 'unknown',
    assetType: 'procedural',
    proceduralId: 'rain-window',
    assetUrl: null,
    thumbnailUrl: null,
    promptIds: ['rainy-window-cinematic', 'rainy-window-minimal'],
    audioEffect: 'room',
    enabled: true,
    weight: 9,
    lastUsed: null,
    usageCount: 0,
    license: { kind: 'procedural', creator: 'BEX LOOP', attribution: 'none required' },
  },
  {
    id: 'train',
    name: 'Train',
    description:
      'Long-distance train window at night. Distant town lights sweep past, faint interior reflection, steady rhythmic sway, empty seat edge in soft focus.',
    tags: ['train', 'travel', 'night', 'window', 'transit'],
    moods: ['NOSTALGIC', 'CALM', 'NIGHT'],
    environments: ['vehicle', 'night'],
    cameraStyle: 'window-fixed',
    visualStyle: 'analog-film',
    duration: 12,
    loopable: true,
    loopQuality: 'unknown',
    assetType: 'none',
    proceduralId: null,
    assetUrl: null,
    thumbnailUrl: null,
    promptIds: ['train-cinematic'],
    audioEffect: 'headphones',
    enabled: true,
    weight: 6,
    lastUsed: null,
    usageCount: 0,
    license: { kind: 'unassigned', creator: null, attribution: null },
  },
  {
    id: 'city-night',
    name: 'City Night',
    description:
      'Wide city skyline at night from a fixed vantage point. Slow traffic streaks on the road below, windows flicker at random, haze around the light sources.',
    tags: ['city', 'night', 'skyline', 'lights', 'urban', 'wide'],
    moods: ['URBAN', 'NIGHT', 'DARK', 'ENERGETIC'],
    environments: ['urban', 'night', 'outdoor'],
    cameraStyle: 'static',
    visualStyle: 'cinematic-realistic',
    duration: 10,
    loopable: true,
    loopQuality: 'seamless',
    assetType: 'procedural',
    proceduralId: 'city-night',
    assetUrl: null,
    thumbnailUrl: null,
    promptIds: ['city-night-cinematic', 'city-night-minimal'],
    audioEffect: 'none',
    enabled: true,
    weight: 10,
    lastUsed: null,
    usageCount: 0,
    license: { kind: 'procedural', creator: 'BEX LOOP', attribution: 'none required' },
  },
  {
    id: 'bedroom',
    name: 'Bedroom',
    description:
      'Dim bedroom at night, lamp on, curtain moving slightly with the window open, soft shadow on the wall, empty bed edge in frame.',
    tags: ['bedroom', 'night', 'calm', 'indoor', 'lamp', 'quiet'],
    moods: ['CALM', 'NIGHT', 'NOSTALGIC'],
    environments: ['indoor', 'night'],
    cameraStyle: 'static',
    visualStyle: 'analog-film',
    duration: 12,
    loopable: true,
    loopQuality: 'unknown',
    assetType: 'none',
    proceduralId: null,
    assetUrl: null,
    thumbnailUrl: null,
    promptIds: ['bedroom-cinematic'],
    audioEffect: 'room',
    enabled: true,
    weight: 5,
    lastUsed: null,
    usageCount: 0,
    license: { kind: 'unassigned', creator: null, attribution: null },
  },
  {
    id: 'office',
    name: 'Office',
    description:
      'Late-night office desk. Monitor glow, scattered notes, city lights far behind the glass wall, empty chair, dust in the desk lamp beam.',
    tags: ['office', 'desk', 'work', 'computer', 'tech', 'night'],
    moods: ['NEWS', 'TECH', 'SERIOUS', 'CALM'],
    environments: ['indoor', 'urban'],
    cameraStyle: 'static',
    visualStyle: 'clean-minimal',
    duration: 10,
    loopable: true,
    loopQuality: 'seamless',
    assetType: 'procedural',
    proceduralId: 'computer-desk',
    assetUrl: null,
    thumbnailUrl: null,
    promptIds: ['office-cinematic', 'office-minimal'],
    audioEffect: 'room',
    enabled: true,
    weight: 8,
    lastUsed: null,
    usageCount: 0,
    license: { kind: 'procedural', creator: 'BEX LOOP', attribution: 'none required' },
  },
  {
    id: 'rooftop',
    name: 'Rooftop',
    description:
      'Rooftop edge at night looking over the city. Slow cloud drift, antenna silhouette, distant light shimmer, wind moving a loose cable.',
    tags: ['rooftop', 'city', 'night', 'sky', 'urban', 'wide'],
    moods: ['URBAN', 'NIGHT', 'SERIOUS', 'DARK'],
    environments: ['outdoor', 'urban', 'night'],
    cameraStyle: 'slow-pan',
    visualStyle: 'cinematic-realistic',
    duration: 12,
    loopable: true,
    loopQuality: 'unknown',
    assetType: 'none',
    proceduralId: null,
    assetUrl: null,
    thumbnailUrl: null,
    promptIds: ['rooftop-cinematic'],
    audioEffect: 'none',
    enabled: true,
    weight: 7,
    lastUsed: null,
    usageCount: 0,
    license: { kind: 'unassigned', creator: null, attribution: null },
  },
  {
    id: 'bus',
    name: 'Bus',
    description:
      'Inside a city bus at night. Interior strip lighting, street lights sliding across the windows, gentle suspension movement, empty seats in soft focus.',
    tags: ['bus', 'transit', 'night', 'city', 'headphones'],
    moods: ['URBAN', 'NIGHT', 'CALM', 'SERIOUS'],
    environments: ['vehicle', 'urban'],
    cameraStyle: 'window-fixed',
    visualStyle: 'documentary',
    duration: 10,
    loopable: true,
    loopQuality: 'unknown',
    assetType: 'none',
    proceduralId: null,
    assetUrl: null,
    thumbnailUrl: null,
    promptIds: ['bus-cinematic'],
    audioEffect: 'headphones',
    enabled: true,
    weight: 6,
    lastUsed: null,
    usageCount: 0,
    license: { kind: 'unassigned', creator: null, attribution: null },
  },
  {
    id: 'airport',
    name: 'Airport',
    description:
      'Airport terminal at night. Long glass wall, runway lights blinking in the distance, reflections on polished floor, distant anonymous silhouettes out of focus.',
    tags: ['airport', 'travel', 'night', 'glass', 'transit'],
    moods: ['SERIOUS', 'CALM', 'FUTURISTIC', 'NIGHT'],
    environments: ['indoor', 'travel', 'night'],
    cameraStyle: 'static',
    visualStyle: 'clean-minimal',
    duration: 12,
    loopable: true,
    loopQuality: 'unknown',
    assetType: 'none',
    proceduralId: null,
    assetUrl: null,
    thumbnailUrl: null,
    promptIds: ['airport-cinematic'],
    audioEffect: 'room',
    enabled: true,
    weight: 6,
    lastUsed: null,
    usageCount: 0,
    license: { kind: 'unassigned', creator: null, attribution: null },
  },
  {
    id: 'night-street',
    name: 'Night Street',
    description:
      'Quiet city street at night after rain. Wet asphalt reflecting shop signs, one distant car passing, steam from a vent, no people in focus.',
    tags: ['street', 'night', 'rain', 'city', 'reflection', 'urban'],
    moods: ['URBAN', 'DARK', 'NIGHT', 'SERIOUS'],
    environments: ['urban', 'night', 'outdoor'],
    cameraStyle: 'slow-pan',
    visualStyle: 'moody-noir',
    duration: 10,
    loopable: true,
    loopQuality: 'unknown',
    assetType: 'none',
    proceduralId: null,
    assetUrl: null,
    thumbnailUrl: null,
    promptIds: ['night-street-cinematic'],
    audioEffect: 'none',
    enabled: true,
    weight: 8,
    lastUsed: null,
    usageCount: 0,
    license: { kind: 'unassigned', creator: null, attribution: null },
  },
  {
    id: 'abstract-tech',
    name: 'Abstract Technology',
    description:
      'Non-representational technology motion: flowing data lines, soft particle drift, subtle grid perspective, no recognisable objects, no text.',
    tags: ['abstract', 'data', 'digital', 'particles', 'tech', 'loop'],
    moods: ['TECH', 'FUTURISTIC', 'ENERGETIC'],
    environments: ['studio', 'abstract'],
    cameraStyle: 'static',
    visualStyle: 'clean-minimal',
    duration: 10,
    loopable: true,
    loopQuality: 'seamless',
    assetType: 'procedural',
    proceduralId: 'abstract-tech',
    assetUrl: null,
    thumbnailUrl: null,
    promptIds: ['abstract-tech-cinematic'],
    audioEffect: 'none',
    enabled: true,
    weight: 7,
    lastUsed: null,
    usageCount: 0,
    license: { kind: 'procedural', creator: 'BEX LOOP', attribution: 'none required' },
  },
];

/** Fields every scene must define. Used by tests and by the scene editor. */
export const SCENE_REQUIRED_FIELDS = [
  'id',
  'name',
  'description',
  'tags',
  'moods',
  'environments',
  'cameraStyle',
  'visualStyle',
  'duration',
  'loopable',
  'loopQuality',
  'assetType',
  'promptIds',
  'audioEffect',
  'enabled',
  'weight',
];

export const LOOP_QUALITIES = ['seamless', 'repeatable', 'unknown'];

export function getSceneById(id, scenes = SCENES) {
  return scenes.find((scene) => scene.id === id) ?? null;
}

export function getScenesByMood(mood, scenes = SCENES) {
  if (!mood) return scenes.slice();
  return scenes.filter((scene) => scene.moods.includes(mood));
}

/** Deep-ish clone so runtime edits never mutate the shipped defaults. */
export function cloneScene(scene) {
  return {
    ...scene,
    tags: [...scene.tags],
    moods: [...scene.moods],
    environments: [...scene.environments],
    promptIds: [...scene.promptIds],
    license: { ...(scene.license ?? {}) },
  };
}

export function allScenes() {
  return SCENES.map(cloneScene);
}
